import { colorStrings } from "@iewccouk/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import XCircle from "@insite/mobius/Icons/XCircle";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { safeColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect, useRef, useState } from "react";
import { css } from "styled-components";

interface Props {
    label: string;
    size: number;
    facetOptions: FacetModel[];
    onRemoveFacet: (facet: FacetModel[]) => void;
    hideDropdown?: boolean;
    showAll?: boolean;
    tagsWrapper?: React.RefObject<HTMLDivElement | null>;
}

export interface SearchFilteringTagStyles {
    wrapper?: InjectableCss;
    tag?: TagPresentationProps;
    tagWrapper?: InjectableCss;
    tagLabel?: TypographyPresentationProps;
    tagSizeLabel?: TypographyPresentationProps;
    tagDivider?: InjectableCss;
    tagButton?: ButtonPresentationProps;
    tagDeleteIcon?: ButtonIconPresentationProps;
    tagDropdownIcon?: ButtonIconPresentationProps;
    optionsWrapper?: InjectableCss;
    option?: InjectableCss;
    optionButton?: ButtonPresentationProps;
    optionLabel?: TypographyPresentationProps;
}

export const searchFilteringTagStyles: SearchFilteringTagStyles = {
    wrapper: {
        css: css`
            position: relative;
        `,
    },
    tag: {
        css: css`
            border-radius: 0;
            height: 40px;
            padding: 4px 8px;
            margin: 0;
        `,
    },
    tagWrapper: {
        css: css`
            align-items: center;
            display: flex;
            gap: 4px;
        `,
    },
    tagLabel: {
        lineHeight: 1,
        css: css`
            min-width: 45px;
        `,
    },
    tagSizeLabel: {
        lineHeight: 1,
    },
    tagDivider: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            height: 32px;
            width: 1px;
        `,
    },
    tagButton: {
        color: colorStrings.commonGlobalWhite,
        buttonType: "outline",
        shape: "rectangle",
        css: css`
            border: 0;
        `,
    },
    tagDeleteIcon: {
        src: XCircle,
        size: 16,
        css: css`
            &&& {
                top: 0;
            }
        `,
    },
    tagDropdownIcon: {
        src: ChevronDown,
        size: 16,
        css: css`
            transition: 0.2s ease-in-out transform;

            &&& {
                top: 0;
            }

            &[aria-expanded="true"] {
                transform: rotate(180deg);
            }
        `,
    },
    optionsWrapper: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            box-shadow: 0 5px 20px 0 ${safeColor(colorStrings.commonGlobalBlack).alpha(0.2).toString()};
            height: 0;
            left: 0;
            min-width: 100%;
            opacity: 0;
            position: absolute;
            top: 0;
            z-index: 999;
            transition: 0.2s ease-in-out opacity, 0.2s ease-in-out top;

            &[aria-expanded="true"] {
                height: auto;
                opacity: 1;
                top: 100%;
            }
        `,
    },
    option: {
        css: css`
            align-items: center;
            display: flex;
            gap: 2px;
            justify-content: space-between;
            padding: 4px 8px;

            &[data-test-selector="facetOption"] {
                & + & {
                    border-top: 1px solid ${getColor(colorStrings.commonGlobalGray04)};
                }
            }
        `,
    },
    optionLabel: {
        variant: "legend",
        forwardAs: "span",
        css: css`
            padding: 0;
        `,
    },
    optionButton: {
        color: colorStrings.secondaryMain,
        buttonType: "outline",
        shape: "rectangle",
        css: css`
            border: 0;
            min-height: auto;
            padding: 0;
        `,
    },
};

const styles = searchFilteringTagStyles;

const SearchFilteringTag: FC<Props> = ({
    label,
    size,
    facetOptions,
    onRemoveFacet,
    hideDropdown,
    showAll,
    tagsWrapper,
}) => {
    const [expandOptions, setExpandOptions] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [hidden, setHidden] = useState(false);

    const handleExpandCollapseElements = () => {
        setExpandOptions(!expandOptions);
    };

    const handleClickOutside = (ev: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(ev.target as any)) {
            setExpandOptions(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (wrapperRef?.current && tagsWrapper?.current) {
            if ((tagsWrapper.current.scrollHeight ?? 0) > 88 && !showAll) {
              setHidden(wrapperRef.current.offsetTop - tagsWrapper.current.offsetTop > 88);
            } else {
                setHidden(false);
            }
        }
    }, [wrapperRef, tagsWrapper]);

    return (
        <StyledWrapper {...styles.wrapper} ref={wrapperRef} aria-hidden={hidden}>
            <Tag {...styles.tag} deletable={false} data-test-selector="searchFiltering">
                <StyledWrapper {...styles.tagWrapper}>
                    <Typography {...styles.tagLabel}>{label}</Typography>

                    {size > 0 && <Typography {...styles.tagSizeLabel}>({size})</Typography>}

                    <Button {...styles.tagButton} onClick={() => onRemoveFacet(facetOptions)}>
                        <VisuallyHidden>{translate("delete")}</VisuallyHidden>
                        <ButtonIcon {...styles.tagDeleteIcon} />
                    </Button>

                    {!hideDropdown && (
                        <>
                            <StyledWrapper {...styles.tagDivider}></StyledWrapper>

                            <Button {...styles.tagButton} onClick={handleExpandCollapseElements}>
                                <VisuallyHidden>{translate("show options")}</VisuallyHidden>
                                <ButtonIcon {...styles.tagDropdownIcon} aria-expanded={expandOptions} />
                            </Button>
                        </>
                    )}
                </StyledWrapper>
            </Tag>

            {!hideDropdown && (
                <StyledWrapper {...styles.optionsWrapper} aria-expanded={expandOptions}>
                    {expandOptions &&
                        facetOptions.map(facet => (
                            <StyledWrapper
                                key={`facetSelect_${facet.id}`}
                                {...styles.option}
                                data-test-selector="facetOption"
                            >
                                <Typography {...styles.optionLabel}>{facet.name}</Typography>

                                <Button {...styles.optionButton} onClick={() => onRemoveFacet([facet])}>
                                    <ButtonIcon {...styles.tagDeleteIcon} />
                                </Button>
                            </StyledWrapper>
                        ))}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default SearchFilteringTag;
